import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, GraduationCap, UserCog, Shield, Mail, Lock } from "lucide-react";
import Emblem from "@/components/Emblem";
import api, { setToken, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { setUser, setViewAs } = useAuth();
  const [busy, setBusy] = useState(null);
  const [allowDemo, setAllowDemo] = useState(false);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [devRole, setDevRole] = useState(null);
  const [devPw, setDevPw] = useState("");
  const [needsVerify, setNeedsVerify] = useState(null);
  const [resendBusy, setResendBusy] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setAllowDemo(!!r.data.allow_demo_login)).catch((e) => console.warn("Could not load /config (allow_demo_login):", e));
  }, []);

  const demoLogin = async (role, password) => {
    setBusy(role);
    try {
      const { data } = await api.post(`/auth/demo-login?role=${role}`, { password });
      if (data.session_token) setToken(data.session_token);
      setViewAs(null);
      setUser(data.user);
      navigate("/", { replace: true });
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Incorrect dev access password.");
      else toast.error("Could not enter demo. Try again.");
      throw e;
    } finally {
      setBusy(null);
    }
  };

  const submitDevPw = async (e) => {
    e.preventDefault();
    try {
      await demoLogin(devRole, devPw);
      setDevRole(null);
      setDevPw("");
    } catch (err) {
      console.debug("Dev login failed (prompt kept open):", err?.response?.status);
      /* error already surfaced via toast in demoLogin; keep the prompt open */
    }
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: creds.email, password: creds.password });
      if (data.session_token) setToken(data.session_token);
      setViewAs(null);
      setUser(data.user);
      navigate("/", { replace: true });
    } catch (err) {
      if (err?.response?.status === 403) {
        setNeedsVerify(creds.email);
        toast.error(apiError(err, "Please verify your email first."));
      } else {
        toast.error(apiError(err, "Invalid email or password."));
      }
    } finally {
      setPwBusy(false);
    }
  };

  const resendVerification = async () => {
    if (!needsVerify) return;
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: needsVerify });
      toast.success("If that account needs verification, a new link is on its way. Check your inbox.");
    } catch (_) {
      toast.success("If that account needs verification, a new link is on its way. Check your inbox.");
    } finally {
      setResendBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex justify-center bg-[var(--rsp-bg)]">
      <div className="w-full max-w-[560px] px-6 flex flex-col items-center text-center">
        <div className="flex-1 flex flex-col items-center justify-center gap-7 pt-16">
          <div className="fade-up">
            <Emblem size={210} />
          </div>
          <div className="fade-up" style={{ animationDelay: "80ms" }}>
            <div className="eyebrow" style={{ letterSpacing: "0.32em", fontSize: "0.86rem" }}>
              Recruit Sustainment Program
            </div>
            <h1 className="heading gold" style={{ fontSize: "4.6rem", letterSpacing: "-0.02em" }}>
              Riverside
            </h1>
            <div className="flex items-center justify-center gap-3 mt-1">
              <span className="h-px w-8 bg-[var(--rsp-border)]" />
              <span className="font-head uppercase tracking-[0.3em] orange text-sm font-bold">
                Paratus Preliator
              </span>
              <span className="h-px w-8 bg-[var(--rsp-border)]" />
            </div>
          </div>
          <p className="text-[var(--rsp-gray)] max-w-sm leading-relaxed fade-up" style={{ animationDelay: "160ms" }}>
            Training schedules, regulations, media, and mission-critical info for RSP Riverside recruits and families.
          </p>
        </div>

        <div className="w-full pb-12 flex flex-col items-center gap-3 fade-up" style={{ animationDelay: "240ms" }}>
          <form onSubmit={passwordLogin} className="w-full max-w-md space-y-3 text-left" data-testid="login-form">
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" />
              <input className="w-full rsp-card pl-10 pr-3 py-3 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm"
                type="email" placeholder="Email" value={creds.email} onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))} required data-testid="login-email" />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" />
              <input className="w-full rsp-card pl-10 pr-3 py-3 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm"
                type="password" placeholder="Password" value={creds.password} onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))} required data-testid="login-password" />
            </div>
            <button type="submit" disabled={pwBusy} className="btn-gold w-full py-3.5 text-base disabled:opacity-60"
              style={{ textTransform: "none", fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 700, letterSpacing: 0 }} data-testid="login-submit">
              {pwBusy ? "Signing in…" : "Sign In"}
            </button>
          </form>
          {needsVerify && (
            <div className="w-full max-w-md rsp-card p-3 border-[var(--rsp-gold)]/40 bg-[var(--rsp-gold)]/5 text-left" data-testid="verify-prompt">
              <p className="text-sm text-[var(--rsp-cream)]">Your email isn't verified yet. Check your inbox for the link.</p>
              <button onClick={resendVerification} disabled={resendBusy} data-testid="resend-verification-btn"
                className="mt-2 text-sm gold underline underline-offset-4 hover:brightness-110 disabled:opacity-60">
                {resendBusy ? "Sending…" : "Resend verification email"}
              </button>
            </div>
          )}
          <div className="w-full max-w-md flex items-center justify-between text-sm">
            <Link to="/register" className="text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)]" data-testid="register-link">Create an account</Link>
            <Link to="/forgot-password" className="gold hover:brightness-110" data-testid="forgot-link">Forgot password?</Link>
          </div>

          <div className="w-full max-w-md flex items-center gap-3 my-2">
            <span className="h-px flex-1 bg-[var(--rsp-border)]" />
            <span className="eyebrow text-[var(--rsp-muted)]">or</span>
            <span className="h-px flex-1 bg-[var(--rsp-border)]" />
          </div>

          <button data-testid="google-login-btn" onClick={googleLogin}
            className="w-full max-w-md py-3.5 text-base flex items-center justify-center gap-3 rounded-[10px] border border-[var(--rsp-border)] text-[var(--rsp-cream)] hover:border-[var(--rsp-gold)]/60 transition-colors"
            style={{ textTransform: "none", fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 700, letterSpacing: 0 }}>
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.5-4.6 2.4-7.5 2.4-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.6 39 16.2 43.5 24 43.5z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5c-.5.4 7-5.1 7-15 0-1.2-.1-2.4-.4-3.5z"/>
            </svg>
            Sign in with Google
          </button>
          <div className="flex items-center gap-2 text-[var(--rsp-muted)] text-sm mt-1">
            <ShieldCheck size={15} className="gold" />
            Access is granted after admin approval
          </div>

          {allowDemo && (
            <div className="w-full max-w-md mt-6 pt-5 border-t border-[var(--rsp-border)] flex flex-col items-center gap-2.5" data-testid="dev-login-section">
              <div className="eyebrow" style={{ letterSpacing: "0.2em" }}>Dev Access · Enter As</div>
              <div className="w-full grid grid-cols-3 gap-2">
                <button data-testid="demo-recruit-btn" disabled={busy} onClick={() => { setDevRole("recruit"); setDevPw(""); }}
                  className="py-2.5 rounded-[10px] border border-[var(--rsp-border)] text-[var(--rsp-gray)] font-head uppercase tracking-wide text-xs flex items-center justify-center gap-1.5 hover:text-[var(--rsp-cream)]">
                  <GraduationCap size={15} /> Recruit
                </button>
                <button data-testid="demo-recruiter-btn" disabled={busy} onClick={() => { setDevRole("recruiter"); setDevPw(""); }}
                  className="py-2.5 rounded-[10px] border border-[var(--rsp-border)] text-[var(--rsp-gray)] font-head uppercase tracking-wide text-xs flex items-center justify-center gap-1.5 hover:text-[var(--rsp-cream)]">
                  <UserCog size={15} /> Recruiter
                </button>
                <button data-testid="demo-admin-btn" disabled={busy} onClick={() => { setDevRole("admin"); setDevPw(""); }}
                  className="py-2.5 rounded-[10px] border border-[var(--rsp-border)] text-[var(--rsp-gray)] font-head uppercase tracking-wide text-xs flex items-center justify-center gap-1.5 hover:text-[var(--rsp-cream)]">
                  <Shield size={15} /> Admin
                </button>
              </div>
            </div>
          )}
        </div>

        <Link to="/overview" data-testid="login-overview-link"
          className="mt-6 text-xs text-[var(--rsp-muted)] hover:text-[var(--rsp-gold)] transition-colors underline underline-offset-4">
          New here? See what RSP Riverside does
        </Link>

        {devRole && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setDevRole(null)} data-testid="dev-password-modal">
            <form onSubmit={submitDevPw} className="rsp-card p-5 w-full max-w-sm space-y-3 text-left" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 font-head uppercase tracking-wide">
                <Lock size={18} className="gold" /> Dev Access · {devRole}
              </div>
              <p className="text-[var(--rsp-gray)] text-sm">Enter the dev access password to continue as <span className="gold capitalize">{devRole}</span>.</p>
              <input autoFocus type="password" value={devPw} onChange={(e) => setDevPw(e.target.value)} placeholder="Dev access password"
                className="w-full rsp-card px-3 py-2.5 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm" data-testid="dev-password-input" />
              <div className="flex gap-2">
                <button type="button" onClick={() => setDevRole(null)} className="flex-1 py-2.5 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)]" data-testid="dev-password-cancel">Cancel</button>
                <button type="submit" disabled={busy} className="btn-gold flex-1 py-2.5 disabled:opacity-60" data-testid="dev-password-submit">{busy ? "Entering…" : "Enter"}</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
